import { Slot } from '@radix-ui/react-slot'
import type { ComponentProps } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center overflow-hidden rounded-full border px-2.5 py-0.5 font-medium whitespace-nowrap outline-none transition-[background-color,border-color,color] [&>svg]:pointer-events-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-invalid:border-destructive aria-invalid:outline-destructive',
  {
    variants: {
      variant: {
        brand: 'border-selection-border bg-accentSecondary text-accentSecondary-foreground',
        default: 'border-selection-border bg-accentSecondary text-accentSecondary-foreground',
        secondary: 'border-border bg-secondary text-secondary-foreground [a&]:hover:bg-muted',
        periwinkle: 'border-accent bg-accent text-accent-foreground',
        botanical: 'border-chart-3/35 bg-chart-3/10 text-foreground',
        filter: 'border-selection-border/25 bg-accentSecondary text-accentSecondary-foreground',
        info: 'border-status-info-border bg-status-info-background text-status-info-foreground',
        success:
          'border-status-success-border bg-status-success-background text-status-success-foreground',
        warning:
          'border-status-warning-border bg-status-warning-background text-status-warning-foreground',
        destructive:
          'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground focus-visible:outline-destructive',
        infoSoft:
          'border-transparent bg-status-info-background text-status-info-foreground shadow-none',
        successSoft:
          'border-transparent bg-status-success-background text-status-success-foreground shadow-none',
        warningSoft:
          'border-transparent bg-status-warning-background text-status-warning-foreground shadow-none',
        destructiveSoft:
          'border-transparent bg-status-destructive-background text-status-destructive-foreground shadow-none',
        mutedSoft: 'border-transparent bg-muted/40 text-muted-foreground shadow-none',
        refundSoft:
          'border-transparent bg-status-warning-background text-muted-foreground shadow-none',
        outline:
          'text-foreground [a&]:hover:bg-accentSecondary [a&]:hover:text-accentSecondary-foreground',
        muted: 'border-border bg-muted text-muted-foreground',
        successGhost:
          'border-transparent bg-transparent text-status-success-foreground shadow-none',
        infoGhost: 'border-transparent bg-transparent text-status-info-foreground shadow-none',
        warningGhost:
          'border-transparent bg-transparent text-status-warning-foreground shadow-none',
        destructiveGhost:
          'border-transparent bg-transparent text-status-destructive-foreground shadow-none',
        mutedGhost: 'border-transparent bg-transparent text-muted-foreground shadow-none',
        refundGhost: 'border-transparent bg-transparent text-muted-foreground shadow-none',
        placeholder:
          'border-dashed border-border/60 bg-muted/15 font-normal italic text-muted-foreground',
      },
      size: {
        micro: 'gap-0.5 px-1 py-0.5 text-[10px] leading-none [&>svg]:size-2',
        xs: 'gap-1 px-1.5 text-xs [&>svg]:size-2',
        sm: 'text-xs [&>svg]:size-3 gap-1',
        md: 'text-sm [&>svg]:size-4 gap-1.5',
        lg: 'text-base [&>svg]:size-5 gap-2',
        comfortable: 'gap-1.5 px-3 py-2 text-sm leading-5 [&>svg]:size-3.5',
        'control-sm': 'h-7 gap-1.5 px-2.5 text-xs [&>svg]:size-3',
        control: 'h-8 gap-1.5 px-3 text-sm [&>svg]:size-4',
        eyebrow:
          'gap-1 px-2.5 py-1 text-[10px] font-semibold tracking-[0.12em] uppercase [&>svg]:size-3',
        placeholder: 'gap-1 px-3 py-1 text-xs',
        status: 'h-6 gap-1.5 px-2.5 py-0 text-xs leading-none [&>svg]:size-3.5',
        statusQuiet:
          'h-6 gap-1.5 border-0 px-0 py-0 text-xs font-normal leading-none [&>svg]:size-3.5',
        choice: 'gap-2 px-3.5 py-1.5 text-sm [&>svg]:size-3.5',
        chip: 'gap-1.5 px-3 py-1 text-xs font-semibold [&>svg]:size-3.5',
        mediaChip: 'gap-1.5 py-0.5 pr-2.5 pl-1 text-xs font-semibold [&>svg]:size-3.5',
        count: 'h-5 min-w-5 gap-0 px-1.5 py-0 text-xs font-semibold tabular-nums',
        statusIcon: 'h-6 w-6 p-0 [&>svg]:size-4',
        statusResponsive: 'h-6 gap-1.5 px-1.5 lg:w-6 lg:gap-0 lg:px-0 [&>svg]:size-4',
      },
      shape: {
        pill: '',
        control: 'rounded-md',
      },
      wrap: {
        true: 'max-w-full justify-start text-left whitespace-normal',
        false: '',
      },
      interactive: {
        true: 'cursor-pointer hover:brightness-95 active:brightness-90 dark:hover:bg-action-pressed dark:hover:brightness-100 dark:active:bg-accentSecondary dark:active:brightness-100',
        false: '',
      },
      elevation: {
        none: 'shadow-none',
        raised: 'shadow-sm',
      },
      indicator: {
        true: 'before:size-1.5 before:shrink-0 before:rounded-full before:bg-current',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
      shape: 'pill',
      wrap: false,
      interactive: false,
      elevation: 'none',
      indicator: false,
    },
  }
)

type BadgeVariantProps = VariantProps<typeof badgeVariants>

export type BadgeVariant = NonNullable<BadgeVariantProps['variant']>

export type BadgeProps = ComponentProps<'span'> &
  BadgeVariantProps & {
    asChild?: boolean
  }

export type BadgeActionProps = ComponentProps<'button'>

export type BadgeButtonProps = ComponentProps<'button'> & Omit<BadgeVariantProps, 'interactive'>

function Badge({
  className = '',
  variant = 'default',
  size = 'sm',
  shape = 'pill',
  wrap = false,
  interactive = false,
  elevation = 'none',
  indicator = false,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-badge=""
      data-slot="badge"
      data-variant={variant}
      data-size={size}
      data-shape={shape}
      data-interactive={interactive ? '' : undefined}
      data-indicator={indicator ? '' : undefined}
      className={cn(
        badgeVariants({ variant, size, shape, wrap, interactive, elevation, indicator }),
        className
      )}
      {...props}
    />
  )
}

function BadgeAction({ className, type = 'button', ...props }: BadgeActionProps) {
  return (
    <button
      {...props}
      type={type}
      data-slot="badge-action"
      className={cn(
        'inline-flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-current/70 outline-none transition-colors hover:bg-background/70 hover:text-current focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring disabled:cursor-not-allowed disabled:text-muted-foreground disabled:hover:bg-transparent',
        className
      )}
    />
  )
}

function BadgeButton({
  className,
  elevation,
  indicator,
  shape,
  size,
  type = 'button',
  variant,
  wrap,
  ...props
}: BadgeButtonProps) {
  return (
    <Badge
      asChild
      interactive
      className={className}
      elevation={elevation}
      indicator={indicator}
      shape={shape}
      size={size}
      variant={variant}
      wrap={wrap}
    >
      <button {...props} type={type} />
    </Badge>
  )
}

export { Badge, BadgeAction, BadgeButton, badgeVariants }
